import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { KeenIcon } from '@/components';
import { Container } from '@/components/container';
import {
  useGetLinkedBoatsQuery,
  useGetUnlinkedBoatsQuery,
  useGetMemberRosterByIdQuery,
  useLinkBoatMutation,
  useUnlinkBoatMutation,
} from '@/services/ClubMembersRosterService';
import { useSnackbar } from 'notistack';
import usePagination from '@/hooks/usePagination';
import { ConfirmationDialog } from '@/components/dialogBox';

const MemberBoatsListing = () => {
  const navigate = useNavigate();
  const params = useParams();
  const rosterId = params.rosterId as string;
  const [showLinkSection, setShowLinkSection] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  // Linked Boats Pagination
  const linkedPagination = usePagination();
  const linkedQueryParams = useMemo(
    () => ({
      page: linkedPagination.pagination.pageIndex + 1,
      limit: linkedPagination.pagination.pageSize,
    }),
    [linkedPagination.pagination]
  );

  // Unlinked Boats Pagination
  const unlinkedPagination = usePagination();
  const unlinkedQueryParams = useMemo(
    () => ({
      page: unlinkedPagination.pagination.pageIndex + 1,
      limit: unlinkedPagination.pagination.pageSize,
      search: unlinkedPagination.debouncedValue,
    }),
    [unlinkedPagination.pagination, unlinkedPagination.debouncedValue]
  );

  const {
    handleTotalItems: handleLinkedTotalItems,
    handleTotalPages: handleLinkedTotalPages,
  } = linkedPagination;

  const {
    handleTotalItems: handleUnlinkedTotalItems,
    handleTotalPages: handleUnlinkedTotalPages,
  } = unlinkedPagination;

  // Queries
  const {
    data: linkedBoatsData,
    isLoading: linkedLoading,
    isFetching: linkedFetching,
  } = useGetLinkedBoatsQuery(
    { id: rosterId, params: linkedQueryParams },
    { skip: !rosterId, refetchOnMountOrArgChange: true }
  );

  const {
    data: unlinkedBoatsData,
    isLoading: unlinkedLoading,
    isFetching: unlinkedFetching,
  } = useGetUnlinkedBoatsQuery(
    { id: rosterId, params: unlinkedQueryParams },
    { skip: !rosterId || !showLinkSection, refetchOnMountOrArgChange: true }
  );

  const { data: memberDetail, isLoading: memberLoading } =
    useGetMemberRosterByIdQuery(rosterId, {
      skip: !rosterId,
    });

  // Update linked pagination totals
  useEffect(() => {
    if (linkedBoatsData) {
      handleLinkedTotalItems(
        linkedBoatsData.totalItems ||
          linkedBoatsData.total_items ||
          linkedBoatsData.total ||
          0
      );
      handleLinkedTotalPages(
        linkedBoatsData.totalPages || linkedBoatsData.total_pages || 1
      );
    }
  }, [handleLinkedTotalItems, handleLinkedTotalPages, linkedBoatsData]);

  // Update unlinked pagination totals
  useEffect(() => {
    if (unlinkedBoatsData) {
      handleUnlinkedTotalItems(
        unlinkedBoatsData.totalItems ||
          unlinkedBoatsData.total_items ||
          unlinkedBoatsData.total ||
          0
      );
      handleUnlinkedTotalPages(
        unlinkedBoatsData.totalPages || unlinkedBoatsData.total_pages || 1
      );
    }
  }, [handleUnlinkedTotalItems, handleUnlinkedTotalPages, unlinkedBoatsData]);

  // UI State
  const [unlinkConfirmState, setUnlinkConfirmState] = useState<{
    isOpen: boolean;
    boatId: string | null;
  }>({ isOpen: false, boatId: null });

  // Mutations
  const [linkBoat, { isLoading: isLinking }] = useLinkBoatMutation();
  const [unlinkBoat, { isLoading: isUnlinking }] = useUnlinkBoatMutation();

  // Handlers
  const handleAddBoat = () => {
    const params = new URLSearchParams();
    if (memberDetail?.club_id) {
      params.append('clubId', memberDetail.club_id);
    }
    const query = params.toString();
    navigate(query ? `/boats/create?${query}` : '/boats/create');
  };

  const handleLinkBoat = async (boatId: string) => {
    try {
      await linkBoat({ id: rosterId, boatId }).unwrap();
      enqueueSnackbar('Boat linked successfully', { variant: 'success' });
      setShowLinkSection(false);
    } catch {
      enqueueSnackbar('Failed to link boat', { variant: 'error' });
    }
  };

  const handleUnlinkBoat = (boatId: string) => {
    setUnlinkConfirmState({ isOpen: true, boatId });
  };

  const handleConfirmUnlink = async () => {
    if (!unlinkConfirmState.boatId) return;
    try {
      await unlinkBoat({
        id: rosterId,
        boatId: unlinkConfirmState.boatId,
      }).unwrap();
      enqueueSnackbar('Boat unlinked successfully', { variant: 'success' });
      setUnlinkConfirmState({ isOpen: false, boatId: null });
    } catch {
      enqueueSnackbar('Failed to unlink boat', { variant: 'error' });
    }
  };

  const isLoading = linkedLoading || memberLoading;

  if (isLoading) {
    return (
      <Container>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-100 contrast:bg-gray-100">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </Container>
    );
  }

  const linkedBoats = linkedBoatsData?.data || [];
  const unlinkedBoats = unlinkedBoatsData?.data || [];

  return (
    <Container>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-100 contrast:bg-gray-100 text-gray-900 dark:text-white contrast:text-white">
        <div className="card min-w-full bg-white dark:bg-gray-100 contrast:bg-gray-100 border border-gray-200">
          {/* Header */}
          <div className="card-header bg-white dark:bg-gray-100 contrast:bg-gray-100 border-b border-gray-200 py-6 px-8 flex flex-wrap gap-4 items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 contrast:bg-blue-900/20 text-blue-600 dark:text-blue-400 contrast:text-blue-400 rounded-xl">
                <KeenIcon icon="ship" className="text-2xl" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-light-100 tracking-tight leading-none">
                  Member Boats
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-light-500 font-medium mt-1">
                  Manage boats for{' '}
                  <span className="text-gray-900 dark:text-white font-semibold">
                    {memberDetail?.user?.name || memberDetail?.name || 'Member'}
                  </span>
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-center items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="btn btn-sm btn-light dark:bg-gray-100 contrast:bg-gray-100 flex items-center gap-2 border border-gray-200 rounded-xl px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-900 contrast:hover:bg-gray-100/40 text-gray-900 dark:text-white contrast:text-white dark:hover:text-white contrast:hover:text-white"
              >
                <KeenIcon
                  icon="arrow-left"
                  className="text-gray-600 dark:text-white contrast:text-white"
                />
                Back
              </button>
              <button
                onClick={() => setShowLinkSection(!showLinkSection)}
                className={`btn btn-sm flex items-center gap-2 rounded-xl px-4 py-2 ${
                  showLinkSection
                    ? 'bg-gray-100 text-gray-900 border border-gray-200'
                    : 'btn-light dark:btn-light contrast:btn-light hover:bg-blue-50 dark:hover:bg-blue-900/10 contrast:hover:bg-blue-900/10 text-blue-600 dark:text-blue-400 contrast:text-blue-400 border-blue-200 dark:border-blue-800/40 contrast:border-blue-800/40'
                }`}
              >
                <KeenIcon
                  icon="link"
                  className={
                    showLinkSection
                      ? 'text-gray-500 dark:text-white contrast:text-white'
                      : 'text-blue-600 dark:text-blue-400 contrast:text-blue-400'
                  }
                />
                {showLinkSection ? 'Cancel Linking' : 'Link Existing Boat'}
              </button>
              <button
                onClick={handleAddBoat}
                className="btn btn-sm btn-light"
              >
                Add New Boat
              </button>
            </div>
          </div>

          {/* Link Boat Section (Conditional) */}
          {showLinkSection && (
            <div className="bg-gray-50 dark:bg-gray-100 contrast:bg-gray-100 border-b border-gray-200 p-6 animate-in slide-in-from-top-2">
              <div className="bg-white dark:bg-gray-950 contrast:bg-gray-950 rounded-2xl shadow-sm border border-gray-200 p-6 max-w-2xl mx-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white contrast:text-white flex items-center gap-2">
                    <KeenIcon
                      icon="search"
                      className="text-blue-500 dark:text-blue-400 contrast:text-blue-400"
                    />
                    Find Boat to Link
                  </h3>
                  <div className="relative group w-48">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <KeenIcon
                        type="filled"
                        icon="magnifier"
                        className="text-gray-400 group-focus-within:text-blue-500 dark:text-gray-300 contrast:text-gray-300 text-xs"
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="Search boat..."
                      value={unlinkedPagination.searchTerm}
                      onChange={unlinkedPagination.handleSearch}
                      className="block w-full rounded-lg border border-gray-300 pl-8 pr-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 shadow-sm bg-gray-50/30 dark:bg-gray-100 contrast:bg-gray-100 text-gray-900 dark:text-white contrast:text-white placeholder:text-gray-400 dark:placeholder:text-gray-400 contrast:placeholder:text-gray-400"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar min-h-[100px] relative">
                  {unlinkedFetching && unlinkedBoats.length > 0 && (
                    <div className="absolute inset-0 bg-white/50 dark:bg-gray-100/60 contrast:bg-gray-100/60 z-10 flex items-center justify-center rounded-xl backdrop-blur-[1px]">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                  )}
                  {unlinkedLoading ||
                  (unlinkedFetching && unlinkedBoats.length === 0) ? (
                    <div className="flex items-center justify-center py-10 scale-75">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : unlinkedBoats.length === 0 ? (
                    <p className="text-gray-500 text-sm italic py-8 text-center bg-gray-50/50 dark:bg-gray-950 contrast:bg-gray-950 rounded-xl border border-dashed border-gray-200">
                      Please add a boat to link.
                    </p>
                  ) : (
                    <>
                      {unlinkedBoats.map((boat) => (
                        <div
                          key={boat.id}
                          className="flex items-center justify-between p-3 rounded-xl border border-gray-100 hover:border-blue-200 dark:hover:border-blue-800/40 contrast:hover:border-blue-800/40 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 contrast:hover:bg-blue-900/10 group transition-all"
                        >
                          <div className="flex flex-col">
                            <span className="text-sm font-bold text-gray-900 dark:text-white contrast:text-white">
                              {boat.name}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gray-500 dark:text-gray-300 contrast:text-gray-300 font-mono">
                                {boat.registration_number}
                              </span>
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-900 contrast:bg-gray-900 text-gray-600 dark:text-gray-300 contrast:text-gray-300 capitalize">
                                {boat.type}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={() => handleLinkBoat(boat.id)}
                            disabled={isLinking}
                            className="btn btn-sm btn-icon btn-light dark:btn-light contrast:btn-light text-blue-600 dark:text-blue-400 contrast:text-blue-400 bg-white dark:bg-gray-950 contrast:bg-gray-950 border border-gray-200 group-hover:border-blue-200 dark:group-hover:border-blue-800/40 contrast:group-hover:border-blue-800/40 group-hover:text-blue-700 dark:group-hover:text-blue-300 contrast:group-hover:text-blue-300 shadow-sm transition-all active:scale-95"
                            title="Link this boat"
                          >
                            {isLinking ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            ) : (
                              <KeenIcon icon="plus" />
                            )}
                          </button>
                        </div>
                      ))}

                      {/* Unlinked Pagination */}
                      {unlinkedPagination.totalItems > 0 && (
                        <div className="flex items-center justify-between mt-4 py-2 border-t border-gray-100">
                          <span className="text-[10px] text-gray-500 dark:text-gray-300 contrast:text-gray-300 font-medium whitespace-nowrap">
                            {unlinkedPagination.pagination.pageIndex *
                              unlinkedPagination.pagination.pageSize +
                              1}{' '}
                            -{' '}
                            {Math.min(
                              (unlinkedPagination.pagination.pageIndex + 1) *
                                unlinkedPagination.pagination.pageSize,
                              unlinkedPagination.totalItems
                            )}{' '}
                            of {unlinkedPagination.totalItems}
                          </span>
                          <div className="flex items-center gap-1 scale-90">
                            <button
                              className="btn btn-icon btn-sm"
                              disabled={
                                unlinkedPagination.pagination.pageIndex === 0 ||
                                unlinkedFetching
                              }
                              onClick={() =>
                                unlinkedPagination.setPagination((p) => ({
                                  ...p,
                                  pageIndex: p.pageIndex - 1,
                                }))
                              }
                              aria-label="Previous page"
                            >
                              <KeenIcon icon="black-left" aria-hidden="true" />
                            </button>
                            <span className="text-[10px] font-bold text-gray-900 dark:text-white contrast:text-white px-2">
                              {unlinkedPagination.pagination.pageIndex + 1} /{' '}
                              {unlinkedPagination.totalPages}
                            </span>
                            <button
                              className="btn btn-icon btn-sm"
                              disabled={
                                unlinkedPagination.pagination.pageIndex >=
                                  unlinkedPagination.totalPages - 1 ||
                                unlinkedFetching
                              }
                              onClick={() =>
                                unlinkedPagination.setPagination((p) => ({
                                  ...p,
                                  pageIndex: p.pageIndex + 1,
                                }))
                              }
                              aria-label="Next page"
                            >
                              <KeenIcon icon="black-right" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Boat Grid */}
          <div className="card-body p-6 bg-gray-50/50 dark:bg-gray-100 contrast:bg-gray-100 min-h-[400px] relative">
            {linkedFetching && linkedBoats.length > 0 && (
              <div className="absolute inset-0 bg-white/30 dark:bg-gray-100/60 contrast:bg-gray-100/60 z-10 flex items-center justify-center backdrop-blur-[1px]">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              </div>
            )}
            {linkedFetching && linkedBoats.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              </div>
            ) : linkedBoats.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-20 h-20 bg-white dark:bg-gray-950 contrast:bg-gray-950 rounded-full flex items-center justify-center mb-4 shadow-sm border border-gray-100">
                  <KeenIcon
                    icon="ship"
                    className="text-4xl text-gray-300 dark:text-gray-400 contrast:text-gray-400"
                  />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white contrast:text-white">
                  No Boats Linked
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-300 contrast:text-wihte/60 mt-2 max-w-sm">
                  This member does not have any boats linked to their profile
                  yet. Use the actions above to add or link a boat.
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-6">
                  {linkedBoats.map((boat) => (
                    <div
                      key={boat.id}
                      className="bg-white dark:bg-gray-950 contrast:bg-gray-950 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow flex flex-col overflow-hidden group"
                    >
                      {/* Header */}
                      <div className="p-5 border-b border-gray-100 bg-white dark:bg-gray-950 contrast:bg-gray-950">
                        <div className="flex justify-between items-start mb-2">
                          <h3
                            className="text-lg font-bold text-gray-900 dark:text-gray-light-100 truncate pr-2"
                            title={boat.name}
                          >
                            {boat.name}
                          </h3>
                          <span className="px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/40 contrast:bg-blue-900/40 text-blue-700 dark:text-blue-200 contrast:text-blue-200 text-[10px] uppercase font-bold border border-blue-100 dark:border-blue-800/40 contrast:border-blue-800/40">
                            {boat.type}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-300 contrast:text-gray-300 font-medium">
                          <KeenIcon icon="information" className="text-xs" />
                          <span className="font-mono text-gray-500 dark:text-gray-300 contrast:text-white/90">
                            {boat.registration_number}
                          </span>
                        </div>
                      </div>

                      {/* Body */}
                      <div className="p-5 space-y-3 bg-gray-50/30 dark:bg-gray-100 contrast:bg-gray-100 flex-1">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-500 dark:text-gray-300 contrast:text-white/90 font-medium text-[10px] uppercase tracking-wider">
                            Length
                          </span>
                          <span className="text-gray-900 dark:text-white contrast:text-white font-bold">
                            {boat.length} {boat.unit}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-500 dark:text-gray-300 contrast:text-white/90 font-medium text-[10px] uppercase tracking-wider">
                            Beam
                          </span>
                          <span className="text-gray-900 dark:text-white contrast:text-white font-bold">
                            {boat.width} {boat.unit}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-500 dark:text-gray-300 contrast:text-white/90 font-medium text-[10px] uppercase tracking-wider">
                            Draft
                          </span>
                          <span className="text-gray-900 dark:text-white contrast:text-white font-bold">
                            {boat.draft} {boat.unit}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="p-4 border-t border-gray-100 bg-white dark:bg-gray-950 contrast:bg-gray-950 flex items-center gap-3">
                        <button
                          onClick={() => {
                            const params = new URLSearchParams();
                            if (boat.club_id) {
                              params.append('clubId', boat.club_id);
                            }
                            navigate(
                              `/boats/edit/${boat.id}?${params.toString()}`
                            );
                          }}
                          className="flex-1 btn btn-sm btn-light dark:btn-light contrast:btn-light border border-gray-200 text-gray-700 dark:text-white contrast:text-white font-bold text-xs rounded-xl hover:bg-gray-50 dark:hover:bg-gray-900 contrast:hover:bg-gray-900 dark:hover:text-black contrast:hover:text-black transition-all hover:scale-[1.02]"
                        >
                          <KeenIcon icon="pencil" className="mr-1 text-xs" />
                          Edit Details
                        </button>
                        <button
                          onClick={() => handleUnlinkBoat(boat.id)}
                          disabled={isUnlinking}
                          className="btn btn-sm btn-icon btn-light dark:btn-light contrast:btn-light text-gray-500 dark:text-gray-300 contrast:text-gray-300 hover:text-red-600 dark:hover:text-red-400 contrast:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 contrast:hover:bg-red-900/10 border border-transparent hover:border-red-100 dark:hover:border-red-800/40 contrast:hover:border-red-800/40 rounded-xl transition-all"
                          title="Unlink Boat"
                        >
                          {isUnlinking ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                          ) : (
                            <KeenIcon icon="trash" />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Linked boats pagination */}
                {linkedPagination.totalItems > 0 && (
                  <div className="card-footer justify-center md:justify-between flex-col md:flex-row gap-3 text-gray-600 dark:text-gray-300 contrast:text-gray-300 text-2sm font-medium">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 dark:text-gray-300 contrast:text-white/90 font-bold">
                        Show
                      </span>
                      <select
                        className="select select-sm w-16 bg-white dark:bg-gray-100 contrast:bg-gray-100 text-gray-900 dark:text-white contrast:text-white border-gray-300"
                        value={linkedPagination.pagination.pageSize}
                        onChange={(e) =>
                          linkedPagination.setPagination((p) => ({
                            ...p,
                            pageIndex: 0,
                            pageSize: Number(e.target.value),
                          }))
                        }
                        aria-label="Rows per page"
                      >
                        {[4, 8, 12, 20].map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                      <span className="text-xs text-gray-500 dark:text-gray-300 contrast:text-white/90 font-bold">
                        per page
                      </span>
                    </div>

                    <div className="flex items-center gap-4">
                      <span className="whitespace-nowrap text-gray-900 dark:text-white contrast:text-white">
                        {linkedPagination.pagination.pageIndex *
                          linkedPagination.pagination.pageSize +
                          1}{' '}
                        -{' '}
                        {Math.min(
                          (linkedPagination.pagination.pageIndex + 1) *
                            linkedPagination.pagination.pageSize,
                          linkedPagination.totalItems
                        )}{' '}
                        of{' '}
                        <span className="text-gray-900 dark:text-white contrast:text-white font-bold">
                          {linkedPagination.totalItems}
                        </span>
                      </span>
                      <div className="pagination">
                        <button
                          className="btn btn-icon btn-sm"
                          disabled={
                            linkedPagination.pagination.pageIndex === 0 ||
                            linkedFetching
                          }
                          onClick={() =>
                            linkedPagination.setPagination((p) => ({
                              ...p,
                              pageIndex: p.pageIndex - 1,
                            }))
                          }
                          aria-label="Previous page"
                        >
                          <KeenIcon icon="black-left" aria-hidden="true" />
                        </button>
                        <div className="flex items-center">
                          {Array.from(
                            { length: linkedPagination.totalPages },
                            (_, i) => i
                          )
                            .filter((i) => {
                              const current =
                                linkedPagination.pagination.pageIndex;
                              return (
                                i === 0 ||
                                i === linkedPagination.totalPages - 1 ||
                                (i >= current - 1 && i <= current + 1)
                              );
                            })
                            .map((pageNum, idx, arr) => (
                              <div key={pageNum} className="flex items-center">
                                {idx > 0 && pageNum !== arr[idx - 1] + 1 && (
                                  <span className="px-2">...</span>
                                )}
                                <button
                                  key={pageNum}
                                  className={`btn btn-sm h-8 w-8 p-0 flex items-center justify-center rounded-lg font-bold transition-all ${linkedPagination.pagination.pageIndex === pageNum ? 'active' : 'hover:bg-gray-100 dark:hover:bg-coal-200 dark:text-white'}`}
                                  onClick={() =>
                                    linkedPagination.setPagination((p) => ({
                                      ...p,
                                      pageIndex: pageNum,
                                    }))
                                  }
                                >
                                  {pageNum + 1}
                                </button>
                              </div>
                            ))}
                        </div>
                        <button
                          className="btn btn-icon btn-sm"
                          disabled={
                            linkedPagination.pagination.pageIndex >=
                              linkedPagination.totalPages - 1 || linkedFetching
                          }
                          onClick={() =>
                            linkedPagination.setPagination((p) => ({
                              ...p,
                              pageIndex: p.pageIndex + 1,
                            }))
                          }
                          aria-label="Next page"
                        >
                          <KeenIcon icon="black-right" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <ConfirmationDialog
          open={unlinkConfirmState.isOpen}
          onClose={() => setUnlinkConfirmState({ isOpen: false, boatId: null })}
          onConfirm={handleConfirmUnlink}
          message="Are you sure you want to unlink this boat? This will remove the association between this member and the boat."
          confirmButtonText="Unlink Boat"
          cancelButtonText="Cancel"
        />
      </div>
    </Container>
  );
};

export default MemberBoatsListing;
